import React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import HideChart from '@/components/HideChart';
import { useClintMediaQuery } from '@/utils/hooks';
import {
  COMPETITIVE_SCORE_INTRO,
  TAB_CITY_COMPARISON,
} from '@/constants/job-title';
import { trackClickEvent } from '@/utils/site-track';
import HelpIcon from '@/assets/job-title/help.svg';

export interface DataTableRowType {
  [key: string]: any;
  height?: number;
  id?: string | number;
  isPinned?: boolean;
}
export type DataTableRowsType = DataTableRowType[];
export interface DataTableColumnType {
  field: string;
  headerName?: string;
  renderHeader?: () => any;
  renderCell?: (value: any, row: DataTableRowType, isPinned?: boolean) => any;
  headerAlign?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  width?: number;
  [key: string]: any;
}
export type DataTableColumnsType = DataTableColumnType[];

interface DataTableProps {
  columns: DataTableColumnsType;
  rows: DataTableRowsType;
  tableRootClass?: string;
  getRowId?: (item: any) => any;
  hideHeader?: boolean;
  headerHeight?: number;
  isCityRankTable?: boolean;
  [key: string]: any;
}

interface TableRowEleProps {
  isCityRankTable?: boolean;
  isPinned?: boolean;
  children: React.ReactNode;
  rowIndex: number;
}

const TableRowEle = (props: TableRowEleProps) => {
  const { isCityRankTable, isPinned = false, rowIndex, children } = props;
  const trRef = React.useRef<HTMLTableRowElement>(null);
  const router = useRouter();

  const handleClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    if (!isCityRankTable) {
      return;
    }
    const linkHref = trRef.current?.getElementsByTagName('a')[0].href || '';
    if (!linkHref) {
      return;
    }
    trackClickEvent(e, true);
    router.push(linkHref);
  };

  return (
    <TableRow
      ref={trRef}
      className={`!px-12 ${
        isCityRankTable ? '!relative hover:!bg-blue-50 !cursor-pointer' : ''
      } ${isPinned ? '!bg-blue-50' : ''}`}
      onClick={handleClick}
      data-ele-group={
        isCityRankTable
          ? `${TAB_CITY_COMPARISON}-comparison_city-link`
          : undefined
      }
      data-ele-id={isCityRankTable ? `city-link_${rowIndex}` : undefined}
      sx={
        isCityRankTable
          ? {
              '&:hover .city-link': { color: '#0050b3' },
            }
          : undefined
      }
    >
      {children}
    </TableRow>
  );
};

const DataTable = (props: DataTableProps) => {
  const {
    tableRootClass = '',
    columns,
    rows,
    getRowId,
    hideHeader,
    headerHeight,
    isCityRankTable,
  } = props;
  const isMobile = useClintMediaQuery();

  return (
    <HideChart isHide={!rows.length} hideTitle="该项">
      <TableContainer
        component={Paper}
        className={`!rounded-lg ${
          isCityRankTable ? '' : '!px-4 tablet:!px-12'
        } ${tableRootClass}`}
        sx={{ [`&.${tableRootClass}`]: { boxShadow: 'none !important' } }}
      >
        <Table>
          <TableHead hidden={hideHeader}>
            <TableRow>
              {columns.map((column, index, array) => (
                <TableCell
                  key={index}
                  align={column.headerAlign ?? column.align}
                  width={column.width}
                  height={headerHeight}
                  data-city={isCityRankTable}
                  className={`!border-none !pr-0 first:!pl-0 data-[city=true]:!pl-0 first:data-[city=true]:!pl-5 last:data-[city=true]:!pr-4 tablet:!pr-4 tablet:!pl-4 tablet:data-[city=true]:!pl-4 tablet:first:data-[city=true]:!pl-12 tablet:last:data-[city=true]:!pr-16`}
                >
                  {column.renderHeader
                    ? column.renderHeader()
                    : column.headerName}
                  {index === array.length - 1 &&
                    isCityRankTable &&
                    !isMobile && (
                      <Tooltip
                        title={
                          <div className="text-black text-opacity-85">
                            <p className="text-sm leading-5.5">
                              <strong>谈职薪资竞争力指数：</strong>
                              <br />
                              指用本职位的薪资在此城市生活的舒适度，指数越高代表该职位在此城市的竞争力越高
                            </p>
                            <p className="mt-3 leading-5">
                              我们的计算方式：
                              <br />
                              {COMPETITIVE_SCORE_INTRO}
                            </p>
                          </div>
                        }
                        disableTouchListener={true}
                        placement="right"
                        className="ml-2 hidden tablet:inline-block"
                        PopperProps={{
                          sx: {
                            '.MuiTooltip-tooltip': {
                              background: '#fff',
                              boxShadow: '0px 8px 15px rgba(0, 0, 0, 0.15)',
                              borderRadius: '4px',
                              maxWidth: '430px',
                              padding: '8px 20px',
                              margin: 0,
                              marginLeft: '17px !important',
                            },
                          },
                        }}
                      >
                        <div className="inline-block h-5 ml-1 relative top-1">
                          <Image
                            src={HelpIcon}
                            width={20}
                            height={20}
                            alt="tooltip icon"
                          />
                        </div>
                      </Tooltip>
                    )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRowEle
                key={getRowId ? getRowId(row) : rowIndex}
                isCityRankTable={isCityRankTable}
                isPinned={row.isPinned}
                rowIndex={rowIndex}
              >
                {columns.map((column, index, array) => (
                  <TableCell
                    key={index}
                    align={column.align}
                    width={row.width}
                    height={row.height}
                    data-city={isCityRankTable}
                    className={`${
                      rowIndex === rows.length - 1
                        ? '!border-none tablet:[&:nth-last-child(2)]:data-[city=true]:!pr-4 tablet:last:data-[city=true]:!pr-16'
                        : ''
                    } !whitespace-nowrap ${
                      row.isPinned && index !== array.length - 1
                        ? '!font-semibold !text-base'
                        : ''
                    } !px-4 data-[city=true]:!border-none data-[city=true]:!pl-0 first:data-[city=true]:!pl-8 last:!pr-0 last:data-[city=false]:!pb-2 last:data-[city=true]:!pr-4 tablet:data-[city=true]:!pl-4 tablet:first:data-[city=true]:!pl-12 tablet:last:!pr-4 tablet:last:data-[city=false]:!pb-4 tablet:[&:nth-last-child(2)]:data-[city=true]:!pr-16`}
                  >
                    {column.renderCell
                      ? column.renderCell(row[column.field], row, row.isPinned)
                      : row[column.field]}
                  </TableCell>
                ))}
                {isCityRankTable && rowIndex !== rows.length - 1 && (
                  <TableCell className="!block !absolute !bottom-0 !right-0 !left-0 !border-b !border-[#f0f0f0] tablet:!ml-6 tablet:!mr-12" />
                )}
              </TableRowEle>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </HideChart>
  );
};

export default DataTable;
